import { createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// Ties a dark page's background to its PageHeader's text color so the two
// can't drift out of sync — wrap dark-themed page content in <DarkPage>
// instead of separately hardcoding a matching bg-[#030712] wrapper div.
const DarkPageContext = createContext(false);

export const DarkPage = ({ children, text = "text-white", className = "" }) => (
  <DarkPageContext.Provider value={true}>
    <div className={`min-h-screen bg-[#030712] ${text} ${className}`}>
      {children}
    </div>
  </DarkPageContext.Provider>
);

const PageHeader = ({ eyebrow, title, description, actions, dark, backTo = null }) => {
  const navigate = useNavigate();
  const contextDark = useContext(DarkPageContext);
  const isDark = dark ?? contextDark;

  const handleBack = () => {
    // Always resolve to an explicit in-app destination so Back can never
    // bounce out to the public landing/auth page (which navigate(-1) did
    // on a freshly-opened session). Project sub-pages pass an explicit
    // backTo; everything else returns to the dashboard.
    navigate(backTo || "/dashboard");
  };

  return (
    <div
      data-testid="page-header"
      className={`border-b border-[#1F2937] px-5 sm:px-8 lg:px-12 py-6 sm:py-8 flex flex-wrap items-start justify-between gap-4 ${
        isDark ? "bg-[#030712]" : ""
      }`}
    >
      <div className="min-w-0 flex-1">
        <button
          onClick={handleBack}
          className="flex items-center gap-1.5 text-xs text-[#7d8590] hover:text-violet-600 transition-colors mb-3 group"
          data-testid="page-back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
          Back
        </button>
        {eyebrow && (
          <div className="text-xs font-medium uppercase tracking-widest text-violet-600 mb-2">
            {eyebrow}
          </div>
        )}
        <h1 className={`font-display font-bold text-xl sm:text-2xl lg:text-3xl tracking-tight leading-tight ${isDark ? "text-white" : "text-gray-900"}`}>
          {title}
        </h1>
        {description && (
          <p className={`mt-2 max-w-2xl text-sm leading-relaxed ${isDark ? "text-[#7d8590]" : "text-gray-500"}`}>
            {description}
          </p>
        )}
      </div>
      {actions && (
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto shrink-0">
          {actions}
        </div>
      )}
    </div>
  );
};

export default PageHeader;
